import { useQuery } from '@apollo/client';
import type { Policy, WorkflowType } from '@communities/content-management/domain';
import { GET_COMMUNITY_POLICIES } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

interface CommunityPoliciesResponse {
  community: {
    id: string;
    policies: Policy[];
  } | null;
}

export function useCommunityPolicies(communityId?: string, workflowType?: WorkflowType) {
  const { data, loading, error, refetch } = useQuery<CommunityPoliciesResponse>(GET_COMMUNITY_POLICIES, {
    variables: { communityId },
    fetchPolicy: 'cache-first',
    skip: !communityId,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  // Filter policies by workflowType if provided (case-insensitive)
  // Shows legacy policies without workflowType on all tabs for backward compatibility
  const policies: Policy[] = useMemo(() => {
    const allPolicies: Policy[] = data?.community?.policies || [];

    if (!workflowType) {
      return allPolicies;
    }

    return allPolicies.filter(policy => !policy.workflowType || policy.workflowType.toLowerCase() === workflowType.toLowerCase());
  }, [data?.community?.policies, workflowType]);

  return {
    policies,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityPolicies;
